/**
 * Retail Support Optimizer - AI/ML Powered Analysis & Recommendations
 * ===================================================================
 * 
 * ML-powered tool to:
 * - Analyze historical retail support impact
 * - Predict future activity effectiveness
 * - Recommend optimal strategies
 * - What-if scenario simulator
 */

import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import GlobalHeader from '../components/GlobalHeader'
import { 
  Brain, 
  TrendingUp, 
  Calculator, 
  Lightbulb, 
  Sparkles,
  BarChart3,
  Target,
  Zap,
  AlertCircle,
  CheckCircle,
  Calendar,
  DollarSign,
  Package,
  Users
} from 'lucide-react'

export default function RetailSupportOptimizer() {
  const [simulatorMode, setSimulatorMode] = useState<'promotion' | 'event' | 'visit'>('promotion')
  const [promoParams, setPromoParams] = useState({
    discount_percent: 15,
    duration_days: 14,
    promo_type: 'Percent Off',
    start_fiscal_week: '202536'
  })
  const [eventParams, setEventParams] = useState({
    event_type: 'Product Launch',
    has_celebrity: false,
    product_focus: 'Skincare'
  })
  const [visitParams, setVisitParams] = useState({
    hours: 3,
    visit_type: 'Brand Support'
  })

  // Fetch optimization recommendations
  const { data: recommendations, isLoading: recsLoading } = useQuery({
    queryKey: ['retail-support-recommendations'],
    queryFn: async () => {
      const response = await fetch('/api/retail-support/optimize-recommendations')
      if (!response.ok) throw new Error('Failed to fetch recommendations')
      return response.json()
    },
    staleTime: 5 * 60 * 1000
  })

  // Get current parameters based on mode
  const getCurrentParams = () => {
    if (simulatorMode === 'promotion') return promoParams
    if (simulatorMode === 'event') return eventParams
    return visitParams
  }

  // Simulate impact prediction - auto-refetches when params change
  const { data: prediction, isLoading: predLoading, refetch: refetchPrediction } = useQuery({
    queryKey: ['retail-support-prediction', simulatorMode, 
               simulatorMode === 'promotion' ? JSON.stringify(promoParams) : 
               simulatorMode === 'event' ? JSON.stringify(eventParams) : 
               JSON.stringify(visitParams)],
    queryFn: async () => {
      const params = getCurrentParams()

      const response = await fetch('/api/retail-support/predict-impact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          activity_type: simulatorMode,
          parameters: params
        })
      })
      if (!response.ok) throw new Error('Failed to predict impact')
      return response.json()
    },
    staleTime: 0,  // Always fetch fresh predictions
    refetchOnMount: false
  })

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  const formatPercent = (value: number) => {
    return `${(value * 100).toFixed(1)}%`
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <GlobalHeader 
        title="Retail Support Optimizer"
        subtitle="AI-powered insights and recommendations for maximizing retail support impact"
      />

      {/* ML-Powered Insights Dashboard */}
      <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-purple-500/10 rounded-lg">
            <Brain className="w-6 h-6 text-purple-500" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">AI Impact Analysis</h2>
            <p className="text-sm text-muted-foreground">
              Machine learning insights from {recommendations?.overview?.total_activities || 0} historical activities
            </p>
          </div>
        </div>

        {recsLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Analyzing historical data...</p>
          </div>
        ) : recommendations?.overview?.data_quality?.sufficient_for_ml ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-background/80 border border-green-500/20 rounded-lg p-4">
              <CheckCircle className="w-8 h-8 text-green-500 mb-2" />
              <h3 className="font-semibold mb-1">Data Ready for ML</h3>
              <p className="text-sm text-muted-foreground">
                {recommendations.overview.total_activities} activities analyzed
              </p>
            </div>
            <div className="bg-background/80 border border-blue-500/20 rounded-lg p-4">
              <TrendingUp className="w-8 h-8 text-blue-500 mb-2" />
              <h3 className="font-semibold mb-1">Predictions Active</h3>
              <p className="text-sm text-muted-foreground">
                ML model providing impact forecasts
              </p>
            </div>
            <div className="bg-background/80 border border-purple-500/20 rounded-lg p-4">
              <Sparkles className="w-8 h-8 text-purple-500 mb-2" />
              <h3 className="font-semibold mb-1">Recommendations</h3>
              <p className="text-sm text-muted-foreground">
                {recommendations?.actionable_recommendations?.length || 0} insights available
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-2">Building ML Foundation</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  {recommendations?.overview?.data_quality?.recommendation}
                </p>
                <p className="text-sm text-muted-foreground">
                  Currently: {recommendations?.overview?.total_activities || 0} activities
                  | Need: 20+ for full ML predictions
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* What-If Simulator */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="border-b border-border bg-muted/30 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calculator className="w-6 h-6 text-primary" />
              <div>
                <h2 className="text-xl font-bold">What-If Simulator</h2>
                <p className="text-sm text-muted-foreground">Predict impact before execution</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSimulatorMode('promotion')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  simulatorMode === 'promotion'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background border border-border hover:bg-accent'
                }`}
              >
                Promotion
              </button>
              <button
                onClick={() => setSimulatorMode('event')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  simulatorMode === 'event'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background border border-border hover:bg-accent'
                }`}
              >
                Event
              </button>
              <button
                onClick={() => setSimulatorMode('visit')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  simulatorMode === 'visit'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background border border-border hover:bg-accent'
                }`}
              >
                Store Visit
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Input Parameters */}
            <div>
              <h3 className="font-semibold mb-4">Configure Parameters</h3>
              
              {simulatorMode === 'promotion' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Discount %</label>
                    <input
                      type="range"
                      min="5"
                      max="50"
                      step="5"
                      value={promoParams.discount_percent}
                      onChange={(e) => setPromoParams({...promoParams, discount_percent: parseInt(e.target.value)})}
                      className="w-full"
                    />
                    <div className="text-2xl font-bold text-primary mt-2">{promoParams.discount_percent}%</div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Duration (days)</label>
                    <input
                      type="range"
                      min="7"
                      max="30"
                      step="7"
                      value={promoParams.duration_days}
                      onChange={(e) => setPromoParams({...promoParams, duration_days: parseInt(e.target.value)})}
                      className="w-full"
                    />
                    <div className="text-lg font-semibold mt-2">{promoParams.duration_days} days</div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Promo Type</label>
                    <select
                      value={promoParams.promo_type}
                      onChange={(e) => setPromoParams({...promoParams, promo_type: e.target.value})}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                    >
                      <option>Percent Off</option>
                      <option>BOGO</option>
                      <option>Dollar Off</option>
                      <option>Loyalty Event</option>
                    </select>
                  </div>
                </div>
              )}

              {simulatorMode === 'event' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Event Type</label>
                    <select
                      value={eventParams.event_type}
                      onChange={(e) => setEventParams({...eventParams, event_type: e.target.value})}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                    >
                      <option>Product Launch</option>
                      <option>Demo</option>
                      <option>Meet & Greet</option>
                      <option>Workshop</option>
                    </select>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={eventParams.has_celebrity}
                        onChange={(e) => setEventParams({...eventParams, has_celebrity: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-medium">Celebrity/Influencer Present</span>
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Product Focus</label>
                    <input
                      type="text"
                      value={eventParams.product_focus}
                      onChange={(e) => setEventParams({...eventParams, product_focus: e.target.value})}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                      placeholder="e.g., Skincare, Haircare"
                    />
                  </div>
                </div>
              )}

              {simulatorMode === 'visit' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Visit Duration (hours)</label>
                    <input
                      type="range"
                      min="1"
                      max="8"
                      step="0.5"
                      value={visitParams.hours}
                      onChange={(e) => setVisitParams({...visitParams, hours: parseFloat(e.target.value)})}
                      className="w-full"
                    />
                    <div className="text-2xl font-bold text-primary mt-2">{visitParams.hours} hours</div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Visit Type</label>
                    <select
                      value={visitParams.visit_type}
                      onChange={(e) => setVisitParams({...visitParams, visit_type: e.target.value})}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                    >
                      <option>Brand Support</option>
                      <option>In Store Support (ISS)</option>
                      <option>ESV</option>
                      <option>Training</option>
                    </select>
                  </div>
                </div>
              )}

              <button
                onClick={() => refetchPrediction()}
                className="w-full mt-6 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
              >
                <Zap className="w-5 h-5" />
                Predict Impact
              </button>
            </div>

            {/* Prediction Results */}
            <div>
              <h3 className="font-semibold mb-4">Predicted Impact</h3>
              
              {predLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-4 text-sm text-muted-foreground">Calculating...</p>
                </div>
              ) : prediction ? (
                <div className="space-y-4">
                  <div className="bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="w-5 h-5 text-green-500" />
                      <span className="text-sm font-medium text-muted-foreground">Revenue Lift</span>
                    </div>
                    <div className="text-3xl font-bold text-green-600">
                      {formatPercent(prediction.predicted_impact.revenue_lift_pct)}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      ≈ {formatCurrency(prediction.predicted_impact.revenue_lift_dollars_estimate)} additional revenue
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="w-5 h-5 text-blue-500" />
                      <span className="text-sm font-medium text-muted-foreground">Units Lift</span>
                    </div>
                    <div className="text-3xl font-bold text-blue-600">
                      {formatPercent(prediction.predicted_impact.units_lift_pct)}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      ≈ {Math.round(prediction.predicted_impact.units_lift_abs_estimate)} additional units
                    </div>
                  </div>

                  <div className="bg-muted/30 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Confidence Level</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        prediction.predicted_impact.confidence === 'high' ? 'bg-green-500/20 text-green-600' :
                        prediction.predicted_impact.confidence === 'medium' ? 'bg-yellow-500/20 text-yellow-600' :
                        'bg-gray-500/20 text-gray-600'
                      }`}>
                        {prediction.predicted_impact.confidence.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Based on {prediction.similar_historical_activities?.length || 0} similar historical activities
                    </div>
                  </div>

                  {prediction.recommendations && Object.keys(prediction.recommendations).length > 1 && (
                    <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Lightbulb className="w-5 h-5 text-purple-500" />
                        <span className="font-semibold">Optimization Tip</span>
                      </div>
                      {prediction.recommendations.optimal_discount && (
                        <p className="text-sm text-muted-foreground">
                          Try {prediction.recommendations.optimal_discount}% for optimal ROI
                        </p>
                      )}
                      {prediction.recommendations.note && (
                        <p className="text-xs text-muted-foreground mt-2">
                          💡 {prediction.recommendations.note}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Calculator className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Adjust parameters and click "Predict Impact"</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Actionable Recommendations */}
      {recommendations?.actionable_recommendations && (
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <Target className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold">Actionable Recommendations</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recommendations.actionable_recommendations.map((rec: any, idx: number) => (
              <div 
                key={idx}
                className={`border rounded-lg p-4 ${
                  rec.priority === 'high' ? 'border-red-500/30 bg-red-500/5' :
                  rec.priority === 'medium' ? 'border-yellow-500/30 bg-yellow-500/5' :
                  'border-border bg-muted/30'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold">{rec.category}</h3>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    rec.priority === 'high' ? 'bg-red-500/20 text-red-600' :
                    rec.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-600' :
                    'bg-gray-500/20 text-gray-600'
                  }`}>
                    {rec.priority.toUpperCase()}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{rec.recommendation}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Historical Performance Summary */}
      {recommendations && (
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <BarChart3 className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold">Historical Performance</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Promotions */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Promotions
              </h3>
              {recommendations.promotions?.key_insights?.map((insight: string, idx: number) => (
                <div key={idx} className="text-sm text-muted-foreground mb-2">
                  • {insight}
                </div>
              ))}
            </div>

            {/* Events */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Events
              </h3>
              {recommendations.events?.key_insights?.map((insight: string, idx: number) => (
                <div key={idx} className="text-sm text-muted-foreground mb-2">
                  • {insight}
                </div>
              ))}
            </div>

            {/* Store Visits */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Store Visits
              </h3>
              {recommendations.store_visits?.key_insights?.map((insight: string, idx: number) => (
                <div key={idx} className="text-sm text-muted-foreground mb-2">
                  • {insight}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

